import React from 'react';
import { productsData } from '../data/mock';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ArrowRight, Zap, Shield, Settings, Cpu, TextSearch, ShieldCheck, Wind } from 'lucide-react';

const ProductsSection = () => {
  const iconMap = {
    0: Zap,
    1: Shield,
    2: Settings,
    3: Cpu
  };

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="products" className="py-10 bg-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center bg-[#cd2d31]/10 border border-[#cd2d31]/20 rounded-full px-4 py-2 mb-6">
            <span className="text-[#cd2d31] font-semibold text-sm">Our Solutions</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            {productsData.title}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {productsData.subtitle}
          </p>
        </div>

        {/* Products Grid */}
        <div className="grid md:grid-cols-2 gap-8">
          {productsData.products.map((product, index) => {
            const IconComponent = iconMap[index];
            return (
              <Card key={product.id} className="group hover:shadow-2xl transition-all duration-300 hover:scale-105 border-0 bg-gradient-to-br from-white to-gray-50/50 overflow-hidden">
                <CardHeader className="pb-4">
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="bg-[#0a8ec5]/10 p-3 rounded-xl group-hover:bg-[#0a8ec5]/20 transition-colors">
                      <IconComponent className="w-8 h-8 text-[#0a8ec5]" />
                    </div>
                    <div>
                      <CardTitle className="text-xl font-bold text-gray-900 group-hover:text-[#0a8ec5] transition-colors">
                        {product.name}
                      </CardTitle>
                    </div>
                  </div>
                  
                  {/* Product Image Placeholder
                  <div className="w-full h-48 bg-gradient-to-br from-[#0a8ec5]/10 to-[#cd2d31]/10 rounded-lg flex items-center justify-center mb-4">
                    <IconComponent className="w-16 h-16 text-[#0a8ec5]/40" />
                  </div> */}
                </CardHeader>
                
                <CardContent className="space-y-4">
                  <p className="text-gray-600 leading-relaxed">
                    {product.description}
                  </p>
                  
                  {/* Features */}
                  <div className="space-y-2">
                    <h4 className="font-semibold text-gray-900 text-sm mb-3">Key Features:</h4>
                    <div className="flex flex-wrap gap-2">
                      {product.features.map((feature, featureIndex) => (
                        <Badge 
                          key={featureIndex} 
                          variant="outline" 
                          className="text-xs border-[#0a8ec5]/30 text-[#0a8ec5] hover:bg-[#0a8ec5]/10"
                        >
                          {feature}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  
                  {/* CTA Button */}
                  <div className="pt-4">
                    <Button 
                      onClick={scrollToContact}
                      className="w-full bg-[#cd2d31] hover:bg-[#b02529] text-white font-semibold py-3 transition-all duration-200 group-hover:shadow-lg group"
                    >
                      Get Quote for {product.name.split(' ')[0]}
                      <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>


        {/* ================================================================== */}
        {/* START: New Highlights Section         */}
        {/* ================================================================== */}
        <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
          
          {/* Highlight 1: CASTLET Franchise */}
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 flex flex-col md:flex-row items-center gap-8 text-white border border-gray-700 shadow-2xl">
            <div className="md:w-1/3 w-full flex-shrink-0">
              {/* IMPORTANT: Replace this src with the actual path to your image */}
              <img 
                src="img/mcs-iii-controller-family-w750h750.png" 
                alt="CASTLET MCS Controller" 
                className="rounded-lg object-cover w-full h-auto shadow-lg"
              />
              
            </div>
            <div className="flex-grow">
              <span className="inline-block bg-red-600/80 text-white text-xs font-semibold px-3 py-1 rounded-full mb-4">
                Exclusive Sole Franchise - India
              </span>
              <h3 className="text-2xl font-bold mb-3">
                CASTLET MCS Controller
              </h3>
              <p className="text-gray-300 text-sm leading-relaxed">
                As the sole authorized partner in India, we provide exclusive sales and service for the complete range of CASTLET's advanced control systems, including the MCS Controller, Keypad, and CANOpener.
              </p>
            </div>
          </div>

          {/* Highlight 2: Proprietary Solutions */}
          <div className="bg-gradient-to-br from-[#0a8ec5]/5 to-white rounded-2xl p-8 border border-gray-200 shadow-xl flex flex-col justify-center">
              <span className="inline-block bg-[#0a8ec5]/10 text-[#0a8ec5] text-xs font-semibold px-3 py-1 rounded-full mb-4 self-start">
                Indigenous Innovation
              </span>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                Proprietary Design & System Expertise
              </h3>
              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <div className="bg-white p-2 border rounded-md shadow-sm">
                    {/* Assuming you use lucide-react, add ShieldCheck icon */}
                    <ShieldCheck className="w-6 h-6 text-[#0a8ec5]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800">Cage Vent Technology</h4>
                    <p className="text-gray-600 text-sm">Our indigenous proprietary design ensures easy and simple application, enhancing system efficiency and maintenance.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                   <div className="bg-white p-2 border rounded-md shadow-sm">
                    {/* Assuming you use lucide-react, add Wind icon */}
                    <Wind className="w-6 h-6 text-[#0a8ec5]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800">Industrial Air Pressure Balancing</h4>
                    <p className="text-gray-600 text-sm">Expert design and implementation for precise air pressure management in any industrial plant, ensuring safety and operational excellence.</p>
                  </div>
                </div>
              </div>
          </div>

        </div>
        {/* ================================================================== */}
        {/* END: New Highlights Section                                      */}
        {/* ================================================================== */}
        {/* ================================================================== */}
        {/* START: ESP Principles Video Highlight                            */}
        {/* ================================================================== */}
        <div className="mt-16 bg-white rounded-2xl p-8 border border-gray-200 shadow-xl flex flex-col items-center text-center">
            <span className="inline-block bg-[#cd2d31]/10 text-[#cd2d31] text-xs font-semibold px-3 py-1 rounded-full mb-4">
              Educational Resource
            </span>
            <h3 className="text-3xl font-bold text-gray-900 mb-4">
              Explore ESP Principles
            </h3>
            <p className="text-gray-600 max-w-2xl mx-auto mb-8 leading-relaxed">
              We have developed this educational video to clearly explain the primary principles of Electrostatic Precipitator (ESP) operation.
            </p>
            
            <div className="w-full max-w-3xl aspect-video rounded-lg overflow-hidden shadow-lg border border-gray-200">
              <iframe
                className="w-full h-full"
                src="https://www.youtube.com/embed/PAIkUDgmTqo"
                title="ESP Principle - Air Control Engineering"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
            <p className="text-sm text-gray-500 mt-4">Video by Air Control Engineering Pvt. Ltd. on YouTube.</p>
        </div>
        {/* ================================================================== */}
        {/* END: ESP Principles Video Highlight                              */}
        {/* ================================================================== */}
        {/* Additional Services Section */}
        <div className="mt-16 bg-gradient-to-r from-[#0a8ec5]/5 to-[#cd2d31]/5 rounded-2xl p-8">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Additional Services</h3>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Beyond equipment supply, we offer comprehensive support services to ensure optimal performance
            </p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-6">
            <div className="bg-white rounded-lg p-6 text-center shadow-md hover:shadow-lg transition-shadow">
              <Settings className="w-12 h-12 text-[#0a8ec5] mx-auto mb-4" />
              <h4 className="font-semibold text-gray-900 mb-2">Installation & Commissioning</h4>
              <p className="text-gray-600 text-sm">Complete turnkey installation with commissioning support</p>
            </div>
            
            <div className="bg-white rounded-lg p-6 text-center shadow-md hover:shadow-lg transition-shadow">
              <Shield className="w-12 h-12 text-[#cd2d31] mx-auto mb-4" />
              <h4 className="font-semibold text-gray-900 mb-2">Annual Maintenance</h4>
              <p className="text-gray-600 text-sm">Comprehensive AMC packages for optimal equipment performance</p>
            </div>
            
            <div className="bg-white rounded-lg p-6 text-center shadow-md hover:shadow-lg transition-shadow">
              <TextSearch className="w-12 h-12 text-[#cd2d31] mx-auto mb-4" />
              <h4 className="font-semibold text-gray-900 mb-2">Inspection & Condition Assessment</h4>
              <p className="text-gray-600 text-sm">On-site inspection and health assessment ensuring continued system efficiency</p>
            </div>
            
            <div className="bg-white rounded-lg p-6 text-center shadow-md hover:shadow-lg transition-shadow">
              <Cpu className="w-12 h-12 text-[#0a8ec5] mx-auto mb-4" />
              <h4 className="font-semibold text-gray-900 mb-2">Technical Consultancy</h4>
              <p className="text-gray-600 text-sm">Expert consultation for air pollution control solutions</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductsSection;